import { createSlice, createSelector } from '@reduxjs/toolkit'
import { Base64 } from 'js-base64'

const initialState = {
    token: null,
    socketId: null,
    hashed_key: null,
    isLoaded: false,
    isSubscribedChannel: false,
    isLoading: false,
    error: null,
    settings: null,
    id: null,
    regions: null,
    regionId: null
}
const tokenStorageKey = 'fh_session_token'

const session = createSlice({
    name: 'session',
    initialState: initialState,
    reducers: {
        requestSession(state) {
            state.isLoading = true
            state.error = null
        },
        requestSessionDone(state) {
            state.isLoading = false
        },
        successToken(state, { payload }) {
            const token = payload?.token ?? null
            state.token = token
        },
        successSocketId(state, { payload }) {
            const socketId = payload.socketId
            state.socketId = socketId
        },
        successSubscribedChannel(state, { payload }) {
            state.isSubscribedChannel = payload
        },
        successHashedKey(state, { payload }) {
            state.hashed_key = payload?.hashed_key ?? null
        },
        successSession(state, { payload }) {
            state.id = payload.id
            state.regionId = payload.region_id ?? null
            state.isLoaded = true
            state.isLoading = false
            state.error = null
        },
        successRegionId(state, { payload }) {
            state.regionId = payload?.regionId ?? null
        },
        setSettings(state, { payload }) {
            state.settings = payload
        },
        failureSession(state, { payload }) {
            state.isLoading = false
            state.error = payload
        },
        successRegions(state, { payload }) {
            state.regions = payload;
        },
        failureRegions(state, { payload }) {
            state.isLoading = false
            state.errorRegions = payload
        },
    },
})

export const createToken = () => async (dispatch, getState, { storage, tokenGenerator }) => {
    if (getState().session.token) {
        return
    }

    const token = await storage.get(tokenStorageKey)
    if (token) {
        dispatch(successToken({ token }))
    } else {
        const token = await tokenGenerator()
        const base64Token = Base64.encode(JSON.stringify({ ...token, timestamp: (new Date).getTime() }))

        await storage.set(tokenStorageKey, base64Token)

        dispatch(successToken({ token: base64Token }))
    }
}

export const createSocketId = ({ socketId }) => async (dispatch, getState) => {
    dispatch(successSocketId({ socketId }))
}

export const subscribedChannel = () => async (dispatch, getState) => {
    dispatch(successSubscribedChannel(true))
}

export const removeToken = ({ onSuccess }) => async (dispatch, getState, { storage, tokenGenerator }) => {
    const token = await tokenGenerator()
    const base64Token = Base64.encode(JSON.stringify({ ...token, timestamp: (new Date).getTime() }))

    await storage.set(tokenStorageKey, base64Token)

    dispatch(successToken({ token: base64Token }))

    if (onSuccess) onSuccess()
}

export const createSession = (withRefresh = false, onSuccess, { forceRefresh = false } = {}) => async (dispatch, getState, { api }) => {
    const { isLoaded, hashed_key, error } = getState().session

    if (!forceRefresh && !withRefresh && isLoaded && !error) {
        return
    }

    try {
        dispatch(requestSession())

        const params = withRefresh ? { refresh: 1 } : {}
        if (!forceRefresh && hashed_key) {
            params.hashed_key = hashed_key
        }

        const response = await api.getSession(params)
        const responseSettings = await api.getSessionSettings(params)

        // save settings
        if (responseSettings.result === 'success') {
            const { settings } = responseSettings
            dispatch(setSettings(settings))
        }

        if (response.result === 'success' && responseSettings.result === 'success') {
            const { session } = response
            dispatch(successSession({ ...session }))
            dispatch(successHashedKey({ hashed_key: response.hashed_key }))

            if (onSuccess) {
                onSuccess(session.client);
            }
            // 304 - data wasn't changed, we don't need to save error
        } else if (response.statusCode === 304) {
            dispatch(requestSessionDone())
        } else {
            dispatch(failureSession(response))
        }
    } catch (err) {
        dispatch(failureSession(err.toString()))
    }
}

export const getRegions = ({ refresh = false, throwError = false }) => async (dispatch, getState, { api }) => {
    try {
        const state = getState().session
        if (!refresh && state.regions) return;

        const response = await api.getRegions()
        if (response.result === 'success') {
            dispatch(successRegions(response.data || []))
        }
    } catch (err) {
        dispatch(failureRegions(err))
        if (throwError) throw err
    }

}

export const setRegion = ({ regionId, onSuccess }) => async (dispatch, getState, { api }) => {
    const state = getState().session
    const prevRegionId = state.regionId;
    if (prevRegionId === regionId) return;
    
    const revertRegion = () => {
        dispatch(successRegionId({ regionId: prevRegionId }));
    }

    try {
        dispatch(successRegionId({ regionId }));

        const response = await api.setRegion({ region_id: regionId })
        if (response.result !== 'success') {
            revertRegion()
        } else {
            onSuccess?.(response);
        }
    } catch (err) {
        revertRegion()
        throw err
    }
}

export const getSessionToken = createSelector(
    state => state.session,
    session => session.token,
)

export const getSessionSocketId = createSelector(
    state => state.session,
    session => session.socketId,
)

export const getSessionData = createSelector(
    state => state.session,
    session => session,
)

export const getSettings = createSelector(
    state => state.session.settings,
    session => session,
)

export const { requestSession, requestSessionDone, successSession, successHashedKey, failureSession, successToken, setSettings, successSocketId, successSubscribedChannel, successRegionId, successRegions, failureRegions } = session.actions

export default session.reducer
